import { useQueryClient } from "@tanstack/react-query";
import { capitalize } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { NotificationsLevel } from "../../lib/enums";
import * as notifications from "../../notifications/notifications";
import LoadingOverlay from "../LoadingOverlay";
import ModalWrapper from "../ModalWrapper";
import { UserClassificationData, UserClassificationFormValues } from "./@types";
import UserClassificationForm from "./UserClassificationForm";
import {
  useDeleteBulkCloudMutation,
  useDeleteBulkLocalMutation,
  useSetBulkCloudMutation,
  useSetBulkLocalMutation
} from "./mutations";
import {
  useCloudUserClassificationQuery,
  useLocalUserClassificationQuery
} from "./queries";

const defaultValues = {
  threat_status: "goodware",
  factor: 0,
  threat_name: "Win32.Malware.Generic"
};

const { cloud_enabled: cloudEnabled } = window.tcbase.consts;

interface Props {
  sampleHashes: Array<string>;
  onClose: () => void;
  onSuccess?: (...args: unknown[]) => void;
  onError?: (hash: string) => void;
  refreshPage?: boolean;
  classification?: string;
  factor?: number;
  threatName?: string;
}

function adjustFactor(classification?: string | null, factor?: number | null) {
  if (
    (classification === "goodware" || classification === "unknown") &&
    factor !== null &&
    factor !== undefined &&
    factor > 5
  ) {
    return 0;
  } else if (
    classification === "suspicious" &&
    factor !== null &&
    factor !== undefined &&
    factor !== 6
  ) {
    return 6;
  } else if (
    classification === "malicious" &&
    factor !== null &&
    factor !== undefined &&
    factor < 7
  ) {
    return 10;
  }
  return factor;
}

const LoadingBlock = (
  <div className="user-classification-popup__loading-container">
    <LoadingOverlay />
    <img
      className="user-classification-popup__placeholder-image"
      src={`${window.tcbase.urls.static}images/user-classification-placeholder.png`}
      alt="loading"
    />
  </div>
);

const renderTabTitle = (title: string, testid: string) => (
  <span data-testid={testid}>{title}</span>
);

const UserClassification: React.FC<Props> = ({
  sampleHashes,
  onClose,
  onSuccess,
  onError,
  refreshPage,
  classification,
  factor,
  threatName
}) => {
  const queryClient = useQueryClient();

  const [tabKey, setTabKey] = useState<"cloud" | "local">(
    cloudEnabled ? "cloud" : "local"
  );

  const [cloudValues, setCloudValues] =
    useState<UserClassificationFormValues | null>(null);
  const [localValues, setLocalValues] =
    useState<UserClassificationFormValues | null>(null);

  const [isUserOverride, setIsUserOverride] = useState({
    local: false,
    cloud: false
  });

  const setAdjustedValues = useCallback(
    (type: "cloud" | "local", incoming: UserClassificationFormValues) => {
      const newValues: UserClassificationFormValues = { ...incoming };
      const cleaned: UserClassificationFormValues = Object.fromEntries(
        Object.entries(newValues).filter(
          ([, v]) => v !== null && v !== undefined
        )
      );
      const prevStatus =
        type === "cloud"
          ? cloudValues?.threat_status
          : localValues?.threat_status;
      if (prevStatus !== cleaned.threat_status) {
        const adj = adjustFactor(cleaned.threat_status, cleaned.factor);
        if (adj !== cleaned.factor) {
          cleaned.factor = adj;
        }
      }
      if (type === "cloud") {
        setCloudValues(cleaned);
      } else {
        setLocalValues(cleaned);
      }
    },
    [cloudValues, localValues]
  );

  const { data: cloudData, isLoading: cloudLoading } =
    useCloudUserClassificationQuery(sampleHashes, cloudEnabled);
  const { data: localData, isLoading: localLoading } =
    useLocalUserClassificationQuery(sampleHashes);

  const setIsUserOverrideFromData = useCallback(
    (type: "cloud" | "local", isOverride: boolean) => {
      setIsUserOverride(prev => ({
        ...prev,
        [type]: isOverride
      }));
    },
    []
  );

  const initValuesFromData = useCallback(
    (type: "cloud" | "local", data: UserClassificationData) => {
      const isUnknown = data.threat_status === "unknown";

      if (type === "cloud") {
        const newCloudValues: UserClassificationFormValues = {
          threat_status: data.threat_status,
          factor: isUnknown ? 0 : data.factor,
          threat_name: data.threat_name
        };
        setAdjustedValues("cloud", newCloudValues);
      } else {
        const fallbackFactor =
          classification === "unknown" ? 0 : data.factor ?? factor;
        const fallbackValues: UserClassificationFormValues = {
          threat_status: data.threat_status ?? classification,
          factor: fallbackFactor,
          threat_name: data.threat_name ?? threatName
        };
        setAdjustedValues("local", fallbackValues);
      }
    },
    [classification, factor, setAdjustedValues, threatName]
  );

  useEffect(() => {
    if (cloudData && !cloudLoading && !cloudValues) {
      initValuesFromData("cloud", cloudData);
      setIsUserOverrideFromData("cloud", !!cloudData.is_user_override);
    }

    const localHasApiData =
      !!localData &&
      localData.threat_status !== null &&
      localData.threat_status !== undefined;
    if (!localHasApiData && !localValues && !localLoading) {
      if (classification) {
        const customVals: UserClassificationFormValues = {
          threat_status: classification,
          factor:
            classification === "unknown" ? 0 : factor ?? defaultValues.factor,
          threat_name: threatName ?? defaultValues.threat_name
        };
        setAdjustedValues("local", customVals);
        setIsUserOverrideFromData("local", false);
      } else if (cloudData && !cloudLoading) {
        const cloned: UserClassificationFormValues = {
          threat_status: cloudData.threat_status,
          factor: cloudData.threat_status === "unknown" ? 0 : cloudData.factor,
          threat_name: cloudData.threat_name
        };
        setAdjustedValues("local", cloned);
      }
    } else if (localHasApiData && !localLoading && !localValues) {
      initValuesFromData("local", localData);
      setIsUserOverrideFromData("local", !!localData.is_user_override);
    }
  }, [
    classification,
    cloudData,
    cloudLoading,
    cloudValues,
    factor,
    initValuesFromData,
    localData,
    localLoading,
    localValues,
    setAdjustedValues,
    setIsUserOverrideFromData,
    threatName
  ]);

  const setBulkCloudMutation = useSetBulkCloudMutation(sampleHashes);
  const deleteBulkCloudMutation = useDeleteBulkCloudMutation(sampleHashes);
  const setBulkLocalMutation = useSetBulkLocalMutation(sampleHashes);
  const deleteBulkLocalMutation = useDeleteBulkLocalMutation(sampleHashes);

  const updateFormValues = useCallback(
    (vals: UserClassificationFormValues) => {
      setAdjustedValues(tabKey, vals);
    },
    [setAdjustedValues, tabKey]
  );

  const currentValues = useMemo(
    () =>
      tabKey === "cloud"
        ? { ...defaultValues, ...cloudValues }
        : { ...defaultValues, ...localValues },
    [tabKey, cloudValues, localValues]
  );
  const [errors, setErrors] = useState<
    Record<"cloud" | "local", Partial<UserClassificationFormValues>>
  >({
    cloud: {},
    local: {}
  });

  const notifySuccess = (view: "cloud" | "local") => {
    const viewDisplay = capitalize(view);
    const cloudMsg =
      " Once set, please wait for changed classification to propagate from the Cloud locally.";
    notifications.show({
      level: NotificationsLevel.SUCCESS,
      message: `${viewDisplay} user classification changed successfully!${
        view === "cloud" ? cloudMsg : ""
      }`
    });
  };

  const handleSuccessAfterAction = (
    view: "cloud" | "local",
    refresh = refreshPage
  ) => {
    notifySuccess(view);
    if (refresh) {
      setTimeout(() => window.location.reload(), 5000);
    }
    onSuccess?.();
    queryClient.invalidateQueries({
      queryKey: ["userClassification", view, sampleHashes]
    });
  };

  const showError = (message?: string) => {
    notifications.show({
      level: NotificationsLevel.ERROR,
      message:
        message ?? "An error occurred while changing user classification."
    });
    onError?.(sampleHashes[0]);
  };

  const showWarning = (message?: string) => {
    notifications.show({
      level: NotificationsLevel.WARNING,
      message:
        message ??
        "An error occurred while changing user classification for some samples."
    });
  };

  const handleSubmit = () => {
    const payload: UserClassificationFormValues = {
      ...defaultValues,
      ...currentValues
    };
    if (
      payload.threat_status === "unknown" ||
      payload.threat_status === "goodware"
    ) {
      payload.threat_name = "";
    }

    const mutation =
      tabKey === "cloud" ? setBulkCloudMutation : setBulkLocalMutation;
    mutation.mutate(payload, {
      onSuccess: r => {
        const hasErrors = Object.keys(r.data?.errors ?? {}).length > 0;
        const hasUpdates = Object.keys(r.data?.updated ?? {}).length > 0;
        const hasNotFound = (r.data?.not_found ?? []).length > 0;

        if (r.status === 200 && !hasErrors) {
          handleSuccessAfterAction(tabKey);
          if (hasNotFound) {
            showWarning(
              "Some selected samples didn't have a classification and were skipped."
            );
          }
          onClose();
        } else if (hasUpdates) {
          showWarning();
          onClose();
        } else {
          showError();
        }
      },
      onError: e => {
        if (e.status === 400 && e.response?.data?.detail?.fields) {
          setErrors(prev => ({
            ...prev,
            [tabKey]: e.response?.data.detail?.fields
          }));
          if (e.response.data.detail.fields.threat_status) {
            setErrors(prev => ({
              ...prev,
              [tabKey]: {
                ...e.response?.data.detail?.fields,
                threat_status: ["Please select a Classification"]
              }
            }));
          }
        } else if (e.status === 400 && e.response?.data?.message) {
          showError(e.response.data.message);
        } else {
          showError();
        }
      }
    });
  };

  const handleRemove = () => {
    const mutation =
      tabKey === "cloud" ? deleteBulkCloudMutation : deleteBulkLocalMutation;
    mutation.mutate(undefined, {
      onSuccess: r => {
        const hasErrors = Object.keys(r.data?.errors ?? {}).length > 0;
        const hasUpdates = Object.keys(r.data?.updated ?? {}).length > 0;
        const hasNotFound = (r.data?.not_found ?? []).length > 0;

        if (r.status === 200 && !hasErrors) {
          handleSuccessAfterAction(tabKey);
          if (hasNotFound) {
            showWarning(
              "Some selected samples didn't have a classification and were skipped."
            );
          }
          onClose();
        } else if (hasUpdates) {
          showWarning();
          onClose();
        } else {
          showError();
        }
      },
      onError: () => {
        showError();
      }
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name } = e.target;
    setErrors(prev => {
      const updated = { ...prev[tabKey] };
      delete updated[name as keyof UserClassificationFormValues];
      return { ...prev, [tabKey]: updated };
    });
  };

  const loading =
    (tabKey === "cloud" ? cloudLoading : localLoading) || !currentValues;

  const formNode = currentValues && (
    <UserClassificationForm
      initial={currentValues}
      values={currentValues}
      errors={errors[tabKey] ?? {}}
      onValues={updateFormValues}
      onChange={handleInputChange}
      onCancel={onClose}
      isUserClassified={isUserOverride[tabKey]}
      onRemove={handleRemove}
      onSubmit={handleSubmit}
      userClassificationType={tabKey}
    />
  );

  return (
    <ModalWrapper
      show
      title="Set Classification"
      onHide={onClose}
      size="lg"
      className={`user-classification-popup modal--borderless ${
        cloudEnabled && "user-classification-popup--tabs"
      }`}
      data-testid="user_classification_modal"
    >
      {cloudEnabled && (
        <Tabs
          id="user-classification-popup-tabs"
          variant="tabs"
          activeKey={tabKey}
          onSelect={(k: "cloud" | "local") => setTabKey(k)}
          transition
          data-testid="user_classification_tabs_container"
        >
          <Tab
            eventKey="cloud"
            title={renderTabTitle(
              "Spectra Intelligence",
              "user_classification_tab_nav_item_cloud"
            )}
            data-testid="user_classification_tab_content_cloud"
          >
            {loading ? LoadingBlock : formNode}
          </Tab>
          <Tab
            eventKey="local"
            title={renderTabTitle(
              "Local",
              "user_classification_tab_nav_item_local"
            )}
            data-testid="user_classification_tab_content_local"
          >
            {loading ? LoadingBlock : formNode}
          </Tab>
        </Tabs>
      )}

      {!cloudEnabled && (loading ? LoadingBlock : formNode)}
    </ModalWrapper>
  );
};

export default UserClassification;
